import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Label,
  Input,
  FormGroup,
  Alert
} from "reactstrap";
import React, { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileDownload,
  faFileUpload,
  faCheck
} from "@fortawesome/free-solid-svg-icons";
import { TimesheetEntry, ExportedTimesheetEntry } from "../../core/types";
import useChanged from "../../hooks/use-changed";
import ShareAPI from "../../core/share";

interface Props {
  show?: boolean;
  entries: TimesheetEntry[];
  sheetName: string;
  latestImport: ExportedTimesheetEntry[];
  onClosed?(): void;
  onImport(
    entries: TimesheetEntry[],
    options: { newSheet: boolean; sheetName: string }
  ): void;
}

const ShareDialog: React.FC<Props> = props => {
  const { show, onClosed, entries, latestImport, sheetName, onImport } = props;

  const [closing, setClosing] = useState(!show);
  const [showAlert, setShowAlert] = useState(false);

  useEffect(() => setClosing(!show), [show]);
  useChanged(() => setShowAlert(true), latestImport);

  return (
    <Modal isOpen={!closing} onClosed={onClosed}>
      <ModalHeader tag="h2" toggle={() => setClosing(true)}>
        Teilen
      </ModalHeader>
      <ModalBody>
        {showAlert ? (
          <Alert>
            <FontAwesomeIcon icon={faCheck} /> {latestImport.length} Einträge
            wurden erfolgreich in das Timesheet "{sheetName}" importiert!
          </Alert>
        ) : (
          <ShareForm
            entries={entries}
            sheetName={sheetName}
            onImport={onImport}
          />
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="link" onClick={() => setClosing(true)}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShareDialog;

ShareDialog.defaultProps = { show: false, onClosed() {} };

interface ShareFormProps {
  entries: TimesheetEntry[];
  sheetName: string;
  onImport(
    entries: TimesheetEntry[],
    options: { newSheet: boolean; sheetName: string }
  ): void;
}

const ShareForm: React.FC<ShareFormProps> = props => {
  const { entries, sheetName, onImport } = props;

  const [importIntoNew, setImportIntoNew] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [file, setFile] = useState<File | null>(null);
  return (
    <>
      <h4>Export</h4>
      <p>
        <Button onClick={() => ShareAPI.exportData(entries, sheetName)}>
          <FontAwesomeIcon icon={faFileDownload} /> Daten herunterladen
        </Button>
      </p>
      <hr />
      <h4>Import</h4>
      <FormGroup>
        <Label>Datei</Label>
        <Input
          type="file"
          onChange={ev => {
            const file = ev.currentTarget.files?.item(0) ?? null;
            setNewSheetName(
              file?.name.substring(0, file?.name?.indexOf(".")) ?? ""
            );
            setFile(ev.currentTarget.files?.item(0) ?? null);
          }}
        />
      </FormGroup>

      <FormGroup tag="fieldset">
        <Label>Optionen</Label>
        <FormGroup check>
          <Label check>
            <Input
              name="options"
              type="radio"
              checked={!importIntoNew}
              onChange={ev => setImportIntoNew(false)}
            />
            in das <strong>aktuelle</strong> Timesheet importieren
          </Label>
        </FormGroup>
        <FormGroup check>
          <Label check>
            <Input
              name="options"
              type="radio"
              checked={importIntoNew}
              onChange={ev => setImportIntoNew(true)}
            />
            in ein <strong>neues</strong> Timesheet importieren
          </Label>
        </FormGroup>
      </FormGroup>

      <FormGroup>
        <Label>Dateiname (optional)</Label>
        <Input
          type="text"
          placeholder="z.B. Projekt, Kunde, etc."
          disabled={!importIntoNew}
          value={newSheetName}
          onChange={ev => setNewSheetName(ev.currentTarget.value)}
        />
      </FormGroup>
      <FormGroup>
        <Button
          disabled={!file}
          title={!file ? "Wählen Sie zuerst eine Datei aus" : ""}
          onClick={async () => {
            const result = await ShareAPI.importData(file!);

            result
              .map(entries =>
                onImport(entries, {
                  newSheet: importIntoNew,
                  sheetName: newSheetName
                })
              )
              .mapError(err => alert(`Fehler: ${err}`));
          }}
        >
          <FontAwesomeIcon icon={faFileUpload} /> Importieren
        </Button>
      </FormGroup>
    </>
  );
};
